import { useState } from 'react'
import { Check, Loader2, MessageSquare, X } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { deviceId, track } from '../lib/analytics'

// Floating feedback button, bottom right (Andrew, 7/28). Writes to lyh_feedback;
// a DB trigger pings Andrew's phone over ntfy, the same channel event
// submissions use. Anonymous by design - the email field is optional and only
// for people who want a reply.
type State = 'idle' | 'sending' | 'done' | 'error'

/**
 * `open`/`onOpenChange` are optional. Uncontrolled, this behaves exactly as it
 * did: its own floating button owns the state. Controlled, the phone's bottom
 * tab bar owns it, because that bar occupies the corner this button used to sit
 * in and two round buttons stacked over the list is not a design.
 */
export function FeedbackWidget({
  open: openProp,
  onOpenChange,
}: {
  open?: boolean
  onOpenChange?: (open: boolean) => void
} = {}) {
  const [openSelf, setOpenSelf] = useState(false)
  const controlled = openProp !== undefined
  const open = controlled ? openProp : openSelf
  const setOpen = (next: boolean) => {
    if (!controlled) setOpenSelf(next)
    onOpenChange?.(next)
  }
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')
  const [state, setState] = useState<State>('idle')

  const toggle = () => {
    if (!open) track('feedback_opened')
    setOpen(!open)
    if (!open) setState('idle')
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (state === 'sending' || !message.trim()) return
    setState('sending')
    const { error } = await supabase.from('lyh_feedback').insert({
      message: message.trim(),
      email: email.trim() || null,
      page: window.location.pathname + window.location.search,
      device_id: deviceId(),
      user_agent: navigator.userAgent.slice(0, 500) || null,
    })
    if (error) {
      setState('error')
      track('feedback_failed', { message: error.message })
      return
    }
    setState('done')
    setMessage('')
    setEmail('')
    track('feedback_submitted', { with_email: email.trim().length > 0 })
  }

  return (
    <>
      {open && (
        <div className="fixed bottom-20 right-4 z-40 w-72 md:bottom-16 rounded-xl border border-slate-200 bg-white p-4 shadow-lg dark:border-slate-800 dark:bg-slate-900">
          {state === 'done' ? (
            <div className="flex flex-col items-center gap-2 py-2 text-center">
              <Check className="h-5 w-5 text-brick-600" />
              <p className="text-sm text-slate-700 dark:text-slate-200">
                Thanks, got it. If you left an email, you&apos;ll hear back.
              </p>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-2">
              {/* The close control used to be the floating button's X. That
                  button is gone (the Feedback tab replaced it), so the panel
                  carries its own or there is no way out of an open form. */}
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Spot an error? Have suggestions? Want to share a positive review?
                </p>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close feedback"
                  className="-mr-1 -mt-1 shrink-0 rounded p-1 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <label htmlFor="fb-message" className="sr-only">
                Your feedback
              </label>
              <textarea
                id="fb-message"
                required
                maxLength={2000}
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="A wrong time, a missing event, an idea..."
                className="w-full resize-none rounded-md border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brick-500 focus:outline-none focus:ring-1 focus:ring-brick-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
              <label htmlFor="fb-email" className="sr-only">
                Email (optional)
              </label>
              <input
                id="fb-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email (optional, if you want a reply)"
                className="w-full rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brick-500 focus:outline-none focus:ring-1 focus:ring-brick-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
              />
              <button
                type="submit"
                disabled={state === 'sending'}
                className="inline-flex items-center justify-center gap-1.5 rounded-md bg-brick-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-brick-700 disabled:opacity-60"
              >
                {state === 'sending' && <Loader2 className="h-4 w-4 animate-spin" />}
                Send
              </button>
              {state === 'error' && (
                <p className="text-xs text-rose-600 dark:text-rose-400">
                  Could not send just now. Please try again.
                </p>
              )}
            </form>
          )}
        </div>
      )}

      {/* Desktop keeps the floating button. On a phone BottomTabs carries
          Feedback and sits exactly where this used to, so two round buttons four
          pixels apart would be the same action twice. */}
      <button
        type="button"
        onClick={toggle}
        aria-label={open ? 'Close feedback' : 'Leave feedback'}
        title="Feedback"
        className="fixed bottom-4 right-4 z-40 hidden h-11 w-11 items-center justify-center rounded-full bg-brick-600 text-white shadow-lg transition-colors hover:bg-brick-700 md:flex"
      >
        {open ? <X className="h-5 w-5" /> : <MessageSquare className="h-5 w-5" />}
      </button>
    </>
  )
}
